import { useState, useEffect, useRef, FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Search, ArrowRight, ArrowUpRight, Menu, X,
  Package, Plane, Warehouse, PackageCheck, FileText,
  Ship, Boxes, ShieldCheck, Clock, Mail, Phone, MapPin,
} from 'lucide-react';
import logo from '../assets/logo.png';
import ScrollToTop from '../components/ScrollToTop';
import { COMPANY } from '@/config/company';

/* ------------------------------------------------------------------ *
 * Reveal-on-scroll. One observer, applied to anything with .reveal.
 * ------------------------------------------------------------------ */
function useReveal() {
  const root = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const nodes = root.current?.querySelectorAll('.reveal');
    if (!nodes?.length) return;

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            io.unobserve(entry.target);
          }
        });
      },
      { rootMargin: '0px 0px -12% 0px', threshold: 0.1 }
    );

    nodes.forEach((n) => io.observe(n));
    return () => io.disconnect();
  }, []);

  return root;
}

/* ------------------------------------------------------------------ *
 * Small shared pieces
 * ------------------------------------------------------------------ */

/** Uppercase mono micro-label — the "waybill" voice used throughout. */
function Label({ children, className = '' }: { children: React.ReactNode; className?: string }) {
  return (
    <span className={`font-mono text-[11px] uppercase tracking-[0.18em] ${className}`}>
      {children}
    </span>
  );
}

/** The live-looking shipment panel in the hero. Shows the product, doesn't describe it. */
function ManifestCard() {
  const stops = [
    { label: 'Collected', done: true },
    { label: 'Customs', done: true },
    { label: 'Transit', done: false, active: true },
    { label: 'Delivered', done: false },
  ];

  return (
    <div className="relative">
      {/* ambient glow */}
      <div
        aria-hidden
        className="absolute -inset-8 rounded-[2rem] bg-gradient-to-br from-blue-600/20 via-purple-600/10 to-transparent blur-2xl"
      />

      <div className="relative rounded-2xl bg-ink-700/80 hairline backdrop-blur-xl overflow-hidden shadow-2xl">
        {/* card header */}
        <div className="flex items-center justify-between px-5 py-3.5 hairline-b bg-white/[0.02]">
          <Label className="text-slate-400">Tracking</Label>
          <div className="flex items-center gap-2">
            <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 animate-node-pulse" />
            <Label className="text-emerald-400">Live</Label>
          </div>
        </div>

        <div className="px-5 pt-5 pb-6">
          <div className="font-mono text-lg text-white tnum tracking-wide">PSS-4417-BH</div>

          {/* route */}
          <div className="mt-5 flex items-end justify-between">
            <div>
              <div className="font-mono text-3xl font-semibold text-white tnum">MIA</div>
              <Label className="text-slate-500">Miami, FL</Label>
            </div>

            <div className="flex-1 px-4 pb-3.5">
              <div className="relative h-px bg-slate-700">
                <div className="absolute inset-y-0 left-0 w-[62%] bg-gradient-to-r from-blue-600 to-[#00a8e8]" />
                {/* current position marker */}
                <span
                  className="absolute top-1/2 h-2 w-2 -translate-x-1/2 -translate-y-1/2 rounded-full bg-[#00a8e8] animate-node-pulse"
                  style={{ left: '62%' }}
                />
              </div>
            </div>

            <div className="text-right">
              <div className="font-mono text-3xl font-semibold text-white tnum">NAS</div>
              <Label className="text-slate-500">Nassau, BS</Label>
            </div>
          </div>

          {/* stops */}
          <div className="mt-7 grid grid-cols-4 gap-2">
            {stops.map((s) => (
              <div key={s.label} className="flex flex-col items-center gap-2">
                <span
                  className={[
                    'h-2 w-2 rounded-full',
                    s.active
                      ? 'bg-[#00a8e8] animate-node-pulse'
                      : s.done
                      ? 'bg-blue-500'
                      : 'bg-slate-700',
                  ].join(' ')}
                />
                <Label
                  className={`whitespace-nowrap text-center leading-tight ${
                    s.active ? 'text-[#00a8e8]' : s.done ? 'text-slate-300' : 'text-slate-600'
                  }`}
                >
                  {s.label}
                </Label>
              </div>
            ))}
          </div>

          {/* meta rows */}
          <dl className="mt-7 space-y-2.5">
            {[
              ['Est. arrival', 'Tomorrow, 4:30 PM'],
              ['Weight', '12.4 lb'],
              ['Service', 'Air freight'],
            ].map(([k, v]) => (
              <div key={k} className="flex items-center justify-between">
                <Label className="text-slate-500">{k}</Label>
                <span className="font-mono text-xs text-slate-200 tnum">{v}</span>
              </div>
            ))}
          </dl>
        </div>
      </div>
    </div>
  );
}

/** Full-bleed route ticker. Reads as a departures board. */
function RouteTicker() {
  const routes = [
    ['MIA', 'NAS', '2 days'],
    ['FPO', 'JFK', '3 days'],
    ['NAS', 'GGT', 'Next day'],
    ['MIA', 'FPO', '2 days'],
    ['ATL', 'NAS', '3 days'],
    ['NAS', 'ELH', 'Next day'],
  ];
  const doubled = [...routes, ...routes];

  return (
    <div className="hairline-t hairline-b bg-ink-800/60 overflow-hidden py-3.5">
      <div className="flex w-max animate-marquee">
        {doubled.map((r, i) => (
          <div key={i} className="flex items-center gap-3 px-8 shrink-0">
            <span className="font-mono text-sm font-semibold text-slate-300 tnum">{r[0]}</span>
            <ArrowRight className="h-3.5 w-3.5 text-blue-500" strokeWidth={2.5} />
            <span className="font-mono text-sm font-semibold text-slate-300 tnum">{r[1]}</span>
            <Label className="text-slate-600">{r[2]}</Label>
            <span className="ml-5 h-1 w-1 rounded-full bg-slate-700" />
          </div>
        ))}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * Page
 * ------------------------------------------------------------------ */
export default function LandingPage() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [tracking, setTracking] = useState('');
  const navigate = useNavigate();
  const root = useReveal();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // No public tracking page exists yet — hand the number to the portal so it
  // survives the login round-trip instead of being retyped.
  const onTrack = (e: FormEvent) => {
    e.preventDefault();
    const id = tracking.trim();
    navigate(id ? `/login?track=${encodeURIComponent(id)}` : '/login');
  };

  const nav = [
    ['Services', '#services'],
    ['How it works', '#how'],
    ['Why us', '#why'],
    ['Contact', '#contact'],
  ];

  const services = [
    {
      icon: Ship,
      index: '01',
      title: 'Freight forwarding',
      copy: 'Air, sea, and land freight with routing and consolidation handled end to end. One point of contact from pickup to final mile.',
      points: ['Air & ocean consolidation', 'Customs documentation', 'Door-to-door routing'],
      wide: true,
    },
    {
      icon: Package,
      index: '02',
      title: 'International shipping',
      copy: 'Parcels and pallets moving between the US and the Bahamas, cleared and tracked the whole way.',
      points: ['Customs clearance', 'Insurance available'],
    },
    {
      icon: Boxes,
      index: '03',
      title: 'Logistics management',
      copy: 'Warehousing, inventory, and scheduled delivery runs for businesses shipping on a cycle.',
      points: ['Warehousing', 'Recurring schedules'],
    },
  ];

  const steps = [
    { icon: FileText, n: '01', title: 'Book it', copy: 'Create the shipment online with addresses, contents, and photos of your invoice.' },
    { icon: Warehouse, n: '02', title: 'We collect', copy: 'Your package is received at our warehouse and logged against your account.' },
    { icon: Plane, n: '03', title: 'In transit', copy: 'Customs paperwork is filed and the shipment moves. Status updates the whole way.' },
    { icon: PackageCheck, n: '04', title: 'Delivered', copy: 'Collected or delivered to your door, with the invoice waiting in your portal.' },
  ];

  // Concrete, checkable promises — deliberately not scale metrics.
  const promises = [
    { k: 'Live tracking', v: 'Every parcel', note: 'Status visible in your portal from intake to hand-off' },
    { k: 'Pricing', v: 'No hidden fees', note: 'Quoted up front, itemised on every invoice' },
    { k: 'Insurance', v: 'On request', note: 'Declared-value coverage available per shipment' },
    { k: 'Support', v: 'Mon–Fri 8–5', note: 'Eastern time, by phone or email' },
  ];

  return (
    <div ref={root} className="min-h-screen bg-ink-900 text-slate-200 antialiased selection:bg-blue-600/40">
      {/* ================= NAV ================= */}
      <header
        className={`fixed inset-x-0 top-0 z-50 transition-all duration-300 ${
          scrolled ? 'bg-ink-900/85 backdrop-blur-xl hairline-b' : 'bg-transparent'
        }`}
      >
        <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4 lg:px-10">
          <Link to="/" className="flex items-center gap-3">
            <img src={logo} alt="" className="h-9 w-9 rounded-lg" />
            <span className="text-[15px] font-semibold tracking-tight text-white">
              Pro Shipping<span className="text-blue-500"> Solutions</span>
            </span>
          </Link>

          <nav className="hidden items-center gap-9 md:flex">
            {nav.map(([label, href]) => (
              <a
                key={href}
                href={href}
                className="font-mono text-[11px] uppercase tracking-[0.18em] text-slate-400 transition-colors hover:text-white"
              >
                {label}
              </a>
            ))}
          </nav>

          <div className="hidden items-center gap-3 md:flex">
            <Link
              to="/login"
              className="rounded-lg px-4 py-2 text-sm font-medium text-slate-300 transition-colors hover:text-white"
            >
              Sign in
            </Link>
            <Link
              to="/customer-register"
              className="group flex items-center gap-1.5 rounded-lg bg-gradient-to-r from-blue-600 to-purple-600 px-4 py-2 text-sm font-semibold text-white shadow-lg shadow-blue-600/20 transition-shadow hover:shadow-blue-600/40"
            >
              Open an account
              <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
          </div>

          <button
            type="button"
            aria-label="Open menu"
            aria-expanded={menuOpen}
            onClick={() => setMenuOpen(true)}
            className="rounded-lg p-2 text-slate-300 hairline md:hidden"
          >
            <Menu className="h-5 w-5" />
          </button>
        </div>
      </header>

      {/* mobile sheet */}
      {menuOpen && (
        <div className="fixed inset-0 z-[60] bg-ink-900/95 backdrop-blur-xl md:hidden">
          <div className="flex items-center justify-between px-6 py-4">
            <span className="text-[15px] font-semibold text-white">Menu</span>
            <button
              type="button"
              aria-label="Close menu"
              onClick={() => setMenuOpen(false)}
              className="rounded-lg p-2 text-slate-300 hairline"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
          <nav className="flex flex-col px-6 pt-6">
            {nav.map(([label, href]) => (
              <a
                key={href}
                href={href}
                onClick={() => setMenuOpen(false)}
                className="hairline-b py-5 text-2xl font-semibold tracking-tight text-white"
              >
                {label}
              </a>
            ))}
            <div className="mt-8 flex flex-col gap-3">
              <Link
                to="/customer-register"
                className="rounded-xl bg-gradient-to-r from-blue-600 to-purple-600 px-5 py-3.5 text-center font-semibold text-white"
              >
                Open an account
              </Link>
              <Link to="/login" className="rounded-xl px-5 py-3.5 text-center font-medium text-slate-300 hairline">
                Sign in
              </Link>
            </div>
          </nav>
        </div>
      )}

      {/* ================= HERO ================= */}
      <section className="relative overflow-hidden pt-32 pb-16 lg:pt-40 lg:pb-24">
        <div aria-hidden className="absolute inset-0 bg-grid" />
        <div
          aria-hidden
          className="absolute left-1/2 top-[-18rem] h-[38rem] w-[62rem] -translate-x-1/2 rounded-full bg-blue-600/15 blur-[130px]"
        />
        <div
          aria-hidden
          className="absolute right-[-14rem] top-32 h-[30rem] w-[30rem] rounded-full bg-purple-600/15 blur-[120px]"
        />
        <div aria-hidden className="absolute inset-x-0 bottom-0 h-40 bg-gradient-to-b from-transparent to-ink-900" />

        <div className="relative mx-auto grid max-w-7xl grid-cols-1 items-center gap-14 px-6 lg:grid-cols-12 lg:gap-10 lg:px-10">
          {/* left */}
          <div className="lg:col-span-7">
            <div className="reveal inline-flex items-center gap-2.5 rounded-full bg-white/[0.04] px-3.5 py-1.5 hairline">
              <MapPin className="h-3.5 w-3.5 text-[#00a8e8]" />
              <Label className="whitespace-nowrap text-slate-300">Nassau, Bahamas</Label>
              <span className="hidden h-3 w-px bg-slate-700 sm:block" />
              <Label className="hidden whitespace-nowrap text-slate-500 sm:block">
                US &amp; Caribbean freight
              </Label>
            </div>

            <h1 className="reveal mt-7 text-[2.75rem] font-extrabold leading-[1.02] tracking-[-0.03em] text-white sm:text-6xl lg:text-7xl">
              Your package,
              <br />
              <span className="bg-gradient-to-r from-blue-500 via-[#00a8e8] to-purple-500 bg-clip-text text-transparent">
                accounted for.
              </span>
            </h1>

            <p className="reveal mt-6 max-w-xl text-lg leading-relaxed text-slate-400">
              Freight forwarding and parcel delivery between the US and the Bahamas — booked online,
              tracked end to end, invoiced in the same place.
            </p>

            {/* the hero CTA is the thing people actually came for */}
            <form onSubmit={onTrack} className="reveal mt-9 max-w-xl">
              <Label className="text-slate-500">Track a shipment</Label>
              <div className="group mt-2.5 flex items-center gap-2 rounded-xl bg-ink-700/70 p-2 hairline backdrop-blur transition-colors focus-within:border-blue-500/60">
                <Search className="ml-2.5 h-4 w-4 shrink-0 text-slate-500" />
                <input
                  value={tracking}
                  onChange={(e) => setTracking(e.target.value)}
                  placeholder="PSS-0000-BH"
                  aria-label="Tracking number"
                  className="min-w-0 flex-1 border-0 bg-transparent p-0 font-mono text-[15px] tracking-wide text-white placeholder:text-slate-600 focus:outline-none focus:ring-0"
                />
                <button
                  type="submit"
                  className="flex shrink-0 items-center gap-1.5 rounded-lg bg-gradient-to-r from-blue-600 to-purple-600 px-5 py-2.5 text-sm font-semibold text-white shadow-lg shadow-blue-600/20 transition-shadow hover:shadow-blue-600/40"
                >
                  Track
                  <ArrowRight className="h-4 w-4" />
                </button>
              </div>
              <p className="mt-3 text-sm text-slate-500">
                Don&apos;t have a number yet?{' '}
                <Link to="/customer-register" className="font-medium text-blue-400 underline-offset-4 hover:underline">
                  Open an account
                </Link>{' '}
                and book your first shipment.
              </p>
            </form>
          </div>

          {/* right */}
          <div className="reveal lg:col-span-5">
            <ManifestCard />
          </div>
        </div>
      </section>

      <RouteTicker />

      {/* ================= PROMISES ================= */}
      <section id="why" className="mx-auto max-w-7xl px-6 py-20 lg:px-10 lg:py-28">
        <div className="reveal max-w-2xl">
          <Label className="text-blue-500">What you get</Label>
          <h2 className="mt-4 text-3xl font-bold tracking-tight text-white sm:text-4xl">
            Four things we&apos;ll hold ourselves to.
          </h2>
        </div>

        <div className="mt-14 grid grid-cols-1 gap-px overflow-hidden rounded-2xl bg-[rgba(148,163,255,0.14)] sm:grid-cols-2 lg:grid-cols-4">
          {promises.map((p, i) => (
            <div
              key={p.k}
              className="reveal group bg-ink-800 p-7 transition-colors hover:bg-ink-700"
              style={{ transitionDelay: `${i * 60}ms` }}
            >
              <Label className="text-slate-500">{p.k}</Label>
              <div className="mt-3 text-2xl font-semibold tracking-tight text-white">{p.v}</div>
              <p className="mt-3 text-sm leading-relaxed text-slate-500">{p.note}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ================= HOW IT WORKS (the route spine) ================= */}
      <section id="how" className="relative bg-ink-800 py-20 hairline-t lg:py-28">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <div className="reveal max-w-2xl">
            <Label className="text-blue-500">How it moves</Label>
            <h2 className="mt-4 text-3xl font-bold tracking-tight text-white sm:text-4xl">
              Four stops between your hands and theirs.
            </h2>
          </div>

          <div className="relative mt-16">
            {/* the rail */}
            <div
              aria-hidden
              className="absolute left-[19px] top-2 bottom-2 w-px bg-gradient-to-b from-blue-500 via-[#00a8e8] to-purple-600 lg:left-[12.5%] lg:right-[12.5%] lg:top-[19px] lg:bottom-auto lg:h-px lg:w-auto lg:bg-gradient-to-r"
            />

            <ol className="grid grid-cols-1 gap-10 lg:grid-cols-4 lg:gap-8">
              {steps.map((s, i) => (
                <li
                  key={s.n}
                  className="reveal relative pl-14 lg:pl-0"
                  style={{ transitionDelay: `${i * 90}ms` }}
                >
                  {/* node */}
                  <div className="absolute left-0 top-0 flex h-10 w-10 items-center justify-center rounded-full bg-ink-800 hairline lg:relative">
                    <s.icon className="h-4 w-4 text-[#00a8e8]" strokeWidth={2} />
                  </div>

                  <div className="lg:mt-7">
                    <div className="flex items-baseline gap-3">
                      <span className="font-mono text-xs text-slate-600 tnum">{s.n}</span>
                      <h3 className="text-lg font-semibold text-white">{s.title}</h3>
                    </div>
                    <p className="mt-2.5 text-sm leading-relaxed text-slate-500">{s.copy}</p>
                  </div>
                </li>
              ))}
            </ol>
          </div>
        </div>
      </section>

      {/* ================= SERVICES ================= */}
      <section id="services" className="mx-auto max-w-7xl px-6 py-20 lg:px-10 lg:py-28">
        <div className="reveal max-w-2xl">
          <Label className="text-blue-500">Services</Label>
          <h2 className="mt-4 text-3xl font-bold tracking-tight text-white sm:text-4xl">
            Three ways we move things.
          </h2>
        </div>

        <div className="mt-14 grid grid-cols-1 gap-5 lg:grid-cols-3">
          {services.map((s, i) => (
            <article
              key={s.title}
              className={`reveal group relative overflow-hidden rounded-2xl bg-ink-800 p-8 hairline transition-colors hover:bg-ink-700 ${
                s.wide ? 'lg:col-span-1 lg:row-span-1' : ''
              }`}
              style={{ transitionDelay: `${i * 80}ms` }}
            >
              <div
                aria-hidden
                className="absolute -right-16 -top-16 h-40 w-40 rounded-full bg-blue-600/10 blur-3xl transition-opacity duration-500 group-hover:bg-purple-600/20"
              />
              <div className="relative">
                <div className="flex items-center justify-between">
                  <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-gradient-to-br from-blue-600/25 to-purple-600/25 hairline">
                    <s.icon className="h-5 w-5 text-[#00a8e8]" strokeWidth={2} />
                  </div>
                  <span className="font-mono text-xs text-slate-700 tnum">{s.index}</span>
                </div>

                <h3 className="mt-6 text-xl font-semibold tracking-tight text-white">{s.title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-slate-400">{s.copy}</p>

                <ul className="mt-6 space-y-2.5">
                  {s.points.map((pt) => (
                    <li key={pt} className="flex items-center gap-2.5">
                      <span className="h-1 w-1 rounded-full bg-blue-500" />
                      <Label className="text-slate-500">{pt}</Label>
                    </li>
                  ))}
                </ul>
              </div>
            </article>
          ))}
        </div>
      </section>

      {/* ================= CONTACT / CTA ================= */}
      <section id="contact" className="relative overflow-hidden bg-ink-800 py-20 hairline-t lg:py-28">
        <div
          aria-hidden
          className="absolute left-1/2 top-0 h-64 w-[50rem] -translate-x-1/2 rounded-full bg-blue-600/15 blur-[120px]"
        />
        <div className="relative mx-auto grid max-w-7xl grid-cols-1 gap-14 px-6 lg:grid-cols-2 lg:gap-20 lg:px-10">
          <div className="reveal">
            <Label className="text-blue-500">Get in touch</Label>
            <h2 className="mt-4 text-3xl font-bold leading-[1.1] tracking-tight text-white sm:text-5xl">
              Ready when
              <br />
              your freight is.
            </h2>
            <p className="mt-6 max-w-md text-lg leading-relaxed text-slate-400">
              Open an account in a couple of minutes, or reach out for a quote on something larger.
            </p>
            <div className="mt-9 flex flex-col gap-3 sm:flex-row">
              <Link
                to="/customer-register"
                className="group flex items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-blue-600 to-purple-600 px-7 py-3.5 font-semibold text-white shadow-lg shadow-blue-600/20 transition-shadow hover:shadow-blue-600/40"
              >
                Open an account
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </Link>
              <a
                href={`mailto:${COMPANY.email}?subject=Shipping%20quote`}
                className="flex items-center justify-center rounded-xl px-7 py-3.5 font-medium text-slate-200 hairline transition-colors hover:bg-white/[0.04]"
              >
                Request a quote
              </a>
            </div>
          </div>

          <div className="reveal divide-y divide-[rgba(148,163,255,0.14)] rounded-2xl bg-ink-700/60 hairline backdrop-blur">
            {[
              { icon: Mail, label: 'Email', value: COMPANY.email, href: `mailto:${COMPANY.email}` },
              { icon: Phone, label: 'Phone', value: COMPANY.phone, href: COMPANY.phoneHref },
              { icon: Clock, label: 'Hours', value: COMPANY.hours },
              { icon: ShieldCheck, label: 'Coverage', value: 'United States · Bahamas · Caribbean' },
            ].map((row) => (
              <div key={row.label} className="flex items-center gap-4 px-6 py-5">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-white/[0.04] hairline">
                  <row.icon className="h-4 w-4 text-[#00a8e8]" strokeWidth={2} />
                </div>
                <div className="min-w-0">
                  <Label className="text-slate-500">{row.label}</Label>
                  {row.href ? (
                    <a
                      href={row.href}
                      className="mt-1 block break-words text-[15px] text-slate-200 transition-colors hover:text-white"
                    >
                      {row.value}
                    </a>
                  ) : (
                    <div className="mt-1 text-[15px] text-slate-200">{row.value}</div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ================= FOOTER ================= */}
      <footer className="bg-ink-900 hairline-t">
        <div className="mx-auto max-w-7xl px-6 py-14 lg:px-10">
          <div className="grid grid-cols-2 gap-10 md:grid-cols-4">
            <div className="col-span-2 md:col-span-1">
              <div className="flex items-center gap-3">
                <img src={logo} alt="" className="h-8 w-8 rounded-lg" />
                <span className="font-semibold text-white">Pro Shipping</span>
              </div>
              <p className="mt-4 max-w-xs text-sm leading-relaxed text-slate-500">
                Freight forwarding and parcel delivery between the US, the Bahamas, and the wider
                Caribbean.
              </p>
            </div>

            <div>
              <Label className="text-slate-600">Services</Label>
              <ul className="mt-4 space-y-3 text-sm text-slate-400">
                <li><a href="#services" className="transition-colors hover:text-white">Freight forwarding</a></li>
                <li><a href="#services" className="transition-colors hover:text-white">International shipping</a></li>
                <li><a href="#services" className="transition-colors hover:text-white">Logistics management</a></li>
              </ul>
            </div>

            <div>
              <Label className="text-slate-600">Company</Label>
              <ul className="mt-4 space-y-3 text-sm text-slate-400">
                <li><a href="#how" className="transition-colors hover:text-white">How it works</a></li>
                <li><a href="#why" className="transition-colors hover:text-white">Why us</a></li>
                <li><a href="#contact" className="transition-colors hover:text-white">Contact</a></li>
              </ul>
            </div>

            <div>
              <Label className="text-slate-600">Account</Label>
              <ul className="mt-4 space-y-3 text-sm text-slate-400">
                <li><Link to="/login" className="transition-colors hover:text-white">Sign in</Link></li>
                <li><Link to="/customer-register" className="transition-colors hover:text-white">Open an account</Link></li>
                <li>
                  <a
                    href={`mailto:${COMPANY.email}?subject=Help`}
                    className="transition-colors hover:text-white"
                  >
                    Help
                  </a>
                </li>
              </ul>
            </div>
          </div>

          <div className="mt-12 flex flex-col items-start justify-between gap-4 pt-8 hairline-t sm:flex-row sm:items-center">
            <Label className="text-slate-600">
              © {new Date().getFullYear()} {COMPANY.name}
            </Label>
            <Label className="text-slate-700">Nassau · Bahamas</Label>
          </div>
        </div>
      </footer>

      <ScrollToTop />
    </div>
  );
}
